import ExcelJS from "exceljs";
import {
	openCostingFabricAssocObjKey,
	openCostingPackingTrimsAssocObjKey,
	openCostingValueAddAssocObjKey,
	openCostingOtherChargesAssocObjkey,
	openCostingPoCostAssocObjkey,
	openCostingSewingTrimsAssocObjKey,
	openCostingTrimsDynamicConsumpAssocObjKey,
} from "../CreateOpenCosting/data";
import { Buffer } from "buffer";
import {
	openCostingChannelWhSplitAssockey,
	openCostingQtySizeSplitAssockey,
} from "../SelectedOpenCosting/Departments/CostingDetails/data";

export const lsFilterkeyStr = "open_costing_filter_obj";

const columns = {
	fabric_details: { name: "Fabric Details", key: "fabric_details", assoc: openCostingFabricAssocObjKey },
	sweing_trims_details: { name: "Sewing Trims Details", key: "sweing_trims_details", assoc: openCostingSewingTrimsAssocObjKey },
	packing_trims_details: { name: "Packing Trims", key: "packing_trims_details", assoc: openCostingPackingTrimsAssocObjKey },
	carton_tape_details: {
		name: "Carton & Tape (Dynamic Consumption)",
		key: "carton_tape_details",
		assoc: openCostingTrimsDynamicConsumpAssocObjKey,
	},
	value_addition_details: { name: "Value Addition", key: "value_addition_details", assoc: openCostingValueAddAssocObjKey },
	garment_total_details: { name: "Garment Total", key: "garment_total_details", assoc: "" },
	final_total_details: { name: "Final Total", key: "final_total_details", assoc: openCostingOtherChargesAssocObjkey },
	po_cost_details: { name: "PO Cost", key: "po_cost_details", assoc: openCostingPoCostAssocObjkey },
};

const wh_columns = {
	qty_size_split: { name: "Qty Size Split", key: "qty_size_split", assoc: openCostingQtySizeSplitAssockey },
	qty_wh_split: { name: "Channel Warehouse Split", key: "qty_wh_split", assoc: openCostingChannelWhSplitAssockey },
};

export const generateCostingDetailsExcel = async (
	costData,
	whSplitData,
	exclude_list = ["last_updated_by", "published_by", "created_by", "published_date", "createdAt", "updatedAt", "status"]
) => {
	try {
		const workbook = new ExcelJS.Workbook();
		const sheet = workbook.addWorksheet("Costing");

		let currentRow = 1;
		let garmentTotal = 0;
		let finalTotalValue = 0;
		let poCostTotalValue = null;

		const formatHeaderLabel = (key) =>
			key.replace(/[_-]+/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

		// --- Basic Info Section ---
		for (const [key, value] of Object.entries(costData)) {
			if (key === "url" || exclude_list.includes(key)) continue;
			if (Array.isArray(value)) continue;

			const keyCell = sheet.getCell(`A${currentRow}`);
			keyCell.value = formatHeaderLabel(key);
			keyCell.font = { bold: true };

			const valueCell = sheet.getCell(`B${currentRow}`);
			valueCell.value = value;
			valueCell.alignment = { horizontal: "center" };

			currentRow++;
		}

		// --- Add Image ---
		try {
			const response = await fetch(costData.url, { cache: "no-store", mode: "cors" });
			if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
			const blob = await response.blob();
			const arrayBuffer = await blob.arrayBuffer();
			const buffer = Buffer.from(arrayBuffer);
			const extMatch = costData.url.match(/\.(jpg|jpeg|png|gif)$/i);
			const extension = extMatch ? extMatch[1].toLowerCase() : "jpeg";
			const imageId = workbook.addImage({ buffer, extension });

			sheet.addImage(imageId, {
				tl: { col: 2.5, row: 1 },
				ext: { width: 190, height: 240 },
			});
		} catch (err) {
			console.error("Image embedding failed:", err);
		}

		// --- Loop through sections ---
		for (const colKey in columns) {
			const { name: title, assoc: association } = columns[colKey];
			let value = costData[association];

			// Skip PO Cost if discounting disabled
			if (
				association === columns["po_cost_details"].assoc &&
				["no", "false", false].includes(String(costData.discounting_applied).toLowerCase())
			) {
				continue;
			}

			if (!Array.isArray(value) || value.length === 0 || typeof value[0] !== "object") continue;

			currentRow += 2;

			const rawKeys = Object.keys(value[0]).filter((h) => !exclude_list.includes(h) && h !== "style_ref_no");
			const endKeys = ["consumption", "price", "value"];
			const orderedKeys = [
				"id",
				"supplier",
				...rawKeys.filter((k) => k !== "id" && k !== "supplier" && !endKeys.includes(k)),
				...endKeys.filter((k) => rawKeys.includes(k)),
			];

			// Title row
			sheet.mergeCells(currentRow, 1, currentRow, orderedKeys.length);
			const titleCell = sheet.getCell(currentRow, 1);
			titleCell.value = title;
			titleCell.font = { bold: true, size: 13 };
			titleCell.alignment = { horizontal: "center" };
			titleCell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FFFAF3D1" },
			};
			currentRow++;

			// Header row
			orderedKeys.forEach((header, i) => {
				const cell = sheet.getCell(currentRow, i + 1);
				cell.value = formatHeaderLabel(header);
				cell.font = { bold: true };
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFD3D3D3" },
				};
				cell.alignment = { horizontal: "center" };
			});
			currentRow++;

			// --- Final Total Section ---
			if (association === columns["final_total_details"].assoc) {
				const categoryIndex = orderedKeys.indexOf("category") + 1;
				const valueIndex = orderedKeys.indexOf("value") + 1;

				const garmentRow = sheet.getRow(currentRow);
				garmentRow.getCell(categoryIndex).value = "Garment Total";
				garmentRow.getCell(categoryIndex).font = { bold: true };
				garmentRow.getCell(categoryIndex).alignment = { horizontal: "center" };
				garmentRow.getCell(valueIndex).value = garmentTotal;
				garmentRow.getCell(valueIndex).font = { bold: true };
				garmentRow.getCell(valueIndex).alignment = { horizontal: "center" };
				currentRow++;

				let finalTotal = garmentTotal;

				value.forEach((item) => {
					orderedKeys.forEach((header, i) => {
						const cell = sheet.getCell(currentRow, i + 1);
						cell.value = item[header];
						cell.alignment = { horizontal: "center" };
					});
					finalTotal += typeof item.value === "number" ? item.value : 0;
					currentRow++;
				});

				// Add TOTAL row inside Final Total section
				const totalRow = currentRow;
				const labelSpan = orderedKeys.findIndex((k) => ["price", "value"].includes(k));
				if (labelSpan > 0) {
					sheet.mergeCells(totalRow, 1, totalRow, labelSpan);
					const labelCell = sheet.getCell(totalRow, 1);
					labelCell.value = "FINAL TOTAL";
					labelCell.font = { bold: true };
					labelCell.alignment = { horizontal: "center" };
					labelCell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "FFF0F0F0" },
					};
				}

				orderedKeys.forEach((key, i) => {
					const col = i + 1;
					const cell = sheet.getCell(totalRow, col);
					if (key === "value") {
						cell.value = finalTotal;
						cell.font = { bold: true };
						cell.alignment = { horizontal: "center" };
					}
				});

				finalTotalValue = finalTotal;
				currentRow += 2;
				continue;
			}

			// --- PO Cost Section (no total row inside) ---
			if (colKey === "po_cost_details") {
				value.forEach((item) => {
					orderedKeys.forEach((header, i) => {
						const cell = sheet.getCell(currentRow, i + 1);
						cell.value = item[header];
						cell.alignment = { horizontal: "center" };
					});
					currentRow++;
				});
				// set the poCostTotalValue to the row which has cost_type as after_discounting.
				poCostTotalValue = value.find((item) => item.cost_type === "after_discounting")?.value;
				// poCostTotalValue = value.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
				currentRow += 2;
				continue;
			}

			// --- Regular Section ---
			value.forEach((item) => {
				orderedKeys.forEach((header, i) => {
					const cell = sheet.getCell(currentRow, i + 1);
					cell.value = item[header];
					cell.alignment = { horizontal: "center" };
				});
				currentRow++;
			});

			// Add TOTAL row for regular sections
			const totalRow = currentRow;
			let priceSum = 0;
			let valueSum = 0;

			value.forEach((item) => {
				priceSum += typeof item.price === "number" ? item.price : 0;
				valueSum += typeof item.value === "number" ? item.value : 0;
			});

			if (colKey !== "final_total_details" && colKey !== "po_cost_details") {
				garmentTotal += valueSum;
			}

			const labelSpan = orderedKeys.findIndex((k) => ["price", "value"].includes(k));
			if (labelSpan > 0) {
				sheet.mergeCells(totalRow, 1, totalRow, labelSpan);
				const labelCell = sheet.getCell(totalRow, 1);
				labelCell.value = "TOTAL";
				labelCell.font = { bold: true };
				labelCell.alignment = { horizontal: "center" };
				labelCell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFF0F0F0" },
				};
			}

			orderedKeys.forEach((key, i) => {
				const col = i + 1;
				const cell = sheet.getCell(totalRow, col);
				if (key === "price") {
					cell.value = priceSum;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
				}
				if (key === "value") {
					cell.value = valueSum;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
				}
			});

			currentRow += 2;
		}

		// --- Final Summary Row ---
		currentRow += 2;
		const summaryRow = sheet.getRow(currentRow);

		summaryRow.getCell(1).value = "FINAL TOTAL";
		summaryRow.getCell(1).font = { bold: true };
		summaryRow.getCell(1).alignment = { horizontal: "center" };
		summaryRow.getCell(1).fill = {
			// <-- add this
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FFF0F0F0" }, // grey color
		};

		summaryRow.getCell(2).value = finalTotalValue || 0;
		summaryRow.getCell(2).font = { bold: true };
		summaryRow.getCell(2).alignment = { horizontal: "center" };

		summaryRow.getCell(3).value = "PO COST";
		summaryRow.getCell(3).font = { bold: true };
		summaryRow.getCell(3).alignment = { horizontal: "center" };
		summaryRow.getCell(3).fill = {
			// <-- add this
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FFF0F0F0" }, // grey color
		};

		const poValue = poCostTotalValue !== null ? poCostTotalValue : finalTotalValue;
		summaryRow.getCell(4).value = poValue || 0;
		summaryRow.getCell(4).font = { bold: true };
		summaryRow.getCell(4).alignment = { horizontal: "center" };

		// --- Column sizing ---
		sheet.getColumn(1).width = 20;
		sheet.getColumn(2).width = 30;
		sheet.getColumn(3).width = 40;

		// --- Warehouse Splits Sheet ---
		const whSheet = workbook.addWorksheet("Warehouse Splits");
		// Extract sizes and channel-splits
		if (whSplitData.result !== null) {
			const sizes = whSplitData.result.sizes;
			const splitRows = whSplitData.result.ocSplitQtyChannelSizeSplit;

			// Group by channels
			const channels = [...new Set(splitRows.map((r) => r.channel))];

			let startCol = 1; // Horizontal placement
			const startRow = 2; // Each table starts at row 2

			for (let channel of channels) {
				const channelData = splitRows.filter((r) => r.channel === channel);

				// ---- Build Headers ----
				let col = startCol;

				// Header Row 1: Channel Name
				whSheet.mergeCells(startRow - 1, col, startRow - 1, col + sizes.length + 2);
				const channelHeader = whSheet.getCell(startRow - 1, col);
				channelHeader.value = `Size Ratio - ${channel}`;
				channelHeader.alignment = { horizontal: "center", vertical: "middle" };
				channelHeader.font = { bold: true, size: 13 };

				// Header Row 2 (Grey Header Row)
				const headerRow = whSheet.getRow(startRow);
				let headers = ["BUY QTY", "STORE GRADE", ...sizes, "TOTAL %"];

				headers.forEach((h, i) => {
					const cell = headerRow.getCell(col + i);
					cell.value = h;
					cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center", vertical: "middle" };
					cell.border = {
						top: { style: "thin", color: { argb: "FFAAAAAA" } },
						left: { style: "thin", color: { argb: "FFAAAAAA" } },
						right: { style: "thin", color: { argb: "FFAAAAAA" } },
						bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
					};
					whSheet.getColumn(col + i).width = 12;
				});

				// ---- Data Row ----
				const dataRow = whSheet.getRow(startRow + 1);

				// BUY QTY (take any size entry)
				const buyQty = channelData.length ? channelData[0].channel_total_qty : 0;
				dataRow.getCell(col).value = buyQty;

				// STORE GRADE
				const grade = channelData.length ? channelData[0].channel_store_grade : "";
				dataRow.getCell(col + 1).value = grade;

				// Size % columns
				let totalPercent = 0;

				sizes.forEach((sizeName, i) => {
					const sizeObj = channelData.find((r) => r.size === sizeName);

					let pct = sizeObj ? sizeObj.size_qty_precent : 0;
					totalPercent += pct;

					const cell = dataRow.getCell(col + 2 + i);
					cell.value = pct / 100; // store actual decimal
					cell.numFmt = "0%";
					cell.alignment = { horizontal: "center" };

					cell.border = {
						top: { style: "thin", color: { argb: "FFDDDDDD" } },
						left: { style: "thin", color: { argb: "FFDDDDDD" } },
						right: { style: "thin", color: { argb: "FFDDDDDD" } },
						bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
					};
				});

				// TOTAL % Column
				const totalCell = dataRow.getCell(col + sizes.length + 2);
				totalCell.value = totalPercent / 100;
				totalCell.numFmt = "0%";
				totalCell.font = { bold: true };
				totalCell.alignment = { horizontal: "center" };

				totalCell.border = {
					top: { style: "thin", color: { argb: "FFAAAAAA" } },
					left: { style: "thin", color: { argb: "FFAAAAAA" } },
					right: { style: "thin", color: { argb: "FFAAAAAA" } },
					bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
				};

				// Move start column to right for next channel table
				startCol += headers.length + 2;
			}

			// =====================================================================
			// SECTION 2: ORDER QTY TABLES (aligned below each channel)
			// =====================================================================

			// row where size ratio tables ended:
			let orderStartBaseRow = startRow + 3; // 2 blank rows

			// Reconstruct starting columns per channel
			let channelStartPositions = {}; // { EBO: 1, MBO: 12, ... }

			startCol = 1;
			channels.forEach((channel) => {
				channelStartPositions[channel] = startCol;

				// Same header count as section 1
				const tempHeaders = ["BUY QTY", "STORE GRADE", ...sizes, "TOTAL %"];
				startCol += tempHeaders.length + 2; // move horizontally
			});

			// Now build Order Qty under each respective channel
			channels.forEach((channel) => {
				const colStart = channelStartPositions[channel];
				const channelData = splitRows.filter((r) => r.channel === channel);

				// Channel Title
				whSheet.mergeCells(orderStartBaseRow, colStart, orderStartBaseRow, colStart + 1);
				const titleCell = whSheet.getCell(orderStartBaseRow, colStart);
				titleCell.value = "Order Qty";
				titleCell.font = { bold: true, size: 13 };
				titleCell.alignment = { horizontal: "center" };

				// Header row
				const headerRow = whSheet.getRow(orderStartBaseRow + 1);
				const headers = ["SIZE", "SIZE QTY"];

				headers.forEach((h, i) => {
					const cell = headerRow.getCell(colStart + i);
					cell.value = h;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
					cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
					cell.border = {
						top: { style: "thin", color: { argb: "FFBBBBBB" } },
						left: { style: "thin", color: { argb: "FFBBBBBB" } },
						right: { style: "thin", color: { argb: "FFBBBBBB" } },
						bottom: { style: "thin", color: { argb: "FFBBBBBB" } },
					};
					whSheet.getColumn(colStart + i).width = 14;
				});

				// TOTAL row
				const totalRow = whSheet.getRow(orderStartBaseRow + 2);
				const totalQty = channelData.length ? channelData[0].channel_total_qty : 0;

				totalRow.getCell(colStart).value = "TOTAL";
				totalRow.getCell(colStart + 1).value = totalQty;

				[colStart, colStart + 1].forEach((c) => {
					const cell = totalRow.getCell(c);
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
					cell.border = {
						top: { style: "thin", color: { argb: "FFDDDDDD" } },
						left: { style: "thin", color: { argb: "FFDDDDDD" } },
						right: { style: "thin", color: { argb: "FFDDDDDD" } },
						bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
					};
				});

				// Size rows
				let rowPointer = orderStartBaseRow + 3;

				for (let sizeName of sizes) {
					const row = whSheet.getRow(rowPointer);
					const sizeObj = channelData.find((s) => s.size === sizeName);

					row.getCell(colStart).value = sizeName;
					row.getCell(colStart + 1).value = sizeObj ? sizeObj.size_qty : 0;

					[colStart, colStart + 1].forEach((c) => {
						const cell = row.getCell(c);
						cell.alignment = { horizontal: "center" };
						cell.border = {
							top: { style: "thin", color: { argb: "FFEEEEEE" } },
							left: { style: "thin", color: { argb: "FFEEEEEE" } },
							right: { style: "thin", color: { argb: "FFEEEEEE" } },
							bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
						};
					});

					rowPointer++;
				}
			});

			// =====================================================================
			// SECTION 3: SIZE CUT QTY RATIO (Manual Mode)
			// =====================================================================

			// Row start: Below the Order Qty tables + 2 gaps
			let cutQtyStartRow = orderStartBaseRow + sizes.length + 6; // dynamic offset

			// ----- Title -----
			whSheet.mergeCells(cutQtyStartRow, 1, cutQtyStartRow, 4);
			const cutTitle = whSheet.getCell(cutQtyStartRow, 1);
			cutTitle.value = "Size Cut Qty Ratio";
			cutTitle.font = { bold: true, size: 14 };
			cutTitle.alignment = { horizontal: "left" };

			// ----- Headers -----
			const cutHeaderRow = whSheet.getRow(cutQtyStartRow + 1);
			const cutHeaders = ["SIZE", "ORDER QUANTITY", "CUT QTY", "CUT QTY %"];

			cutHeaders.forEach((h, i) => {
				const cell = cutHeaderRow.getCell(1 + i);
				cell.value = h;
				cell.font = { bold: true };
				cell.alignment = { horizontal: "center" };
				cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
				cell.border = {
					top: { style: "thin", color: { argb: "FFBBBBBB" } },
					left: { style: "thin", color: { argb: "FFBBBBBB" } },
					right: { style: "thin", color: { argb: "FFBBBBBB" } },
					bottom: { style: "thin", color: { argb: "FFBBBBBB" } },
				};
				whSheet.getColumn(1 + i).width = 18;
			});

			// ----------- Compute Order Quantity per Size -----------

			const sizeOrderTotals = {};
			sizes.forEach((size) => (sizeOrderTotals[size] = 0));

			splitRows.forEach((row) => {
				sizeOrderTotals[row.size] += row.size_qty;
			});

			// ----------- Map CUT QTY from API -----------

			const cutSplit = whSplitData.result.ocSplitQtyCutQtySplit;

			// ----------- Fill Table Rows -----------

			let rowPtr = cutQtyStartRow + 2;

			sizes.forEach((sizeName) => {
				const row = whSheet.getRow(rowPtr);

				// ORDER QUANTITY (sum across all channels)
				const totalOrderQty = sizeOrderTotals[sizeName] || 0;

				// CUT QTY values
				const cutObj = cutSplit.find((s) => s.size === sizeName);
				const cutQty = cutObj ? cutObj.cut_qty : 0;
				const cutPct = cutObj ? cutObj.cut_qty_percent : 0;

				row.getCell(1).value = sizeName;
				row.getCell(2).value = totalOrderQty;
				row.getCell(3).value = cutQty;
				row.getCell(4).value = cutPct / 100; // percent formatting

				row.getCell(4).numFmt = "0%";

				// style all cells
				for (let c = 1; c <= 4; c++) {
					const cell = row.getCell(c);
					cell.alignment = { horizontal: "center" };
					cell.border = {
						top: { style: "thin", color: { argb: "FFEEEEEE" } },
						left: { style: "thin", color: { argb: "FFEEEEEE" } },
						right: { style: "thin", color: { argb: "FFEEEEEE" } },
						bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
					};
				}

				rowPtr++;
			});

			// =====================================================================
			// SECTION 4: CHANNEL-WISE CUT QTY
			// =====================================================================

			// Start 2 rows after Section 3
			let cutChannelStartRow = rowPtr + 3;

			// Use the same horizontal positions (channelStartPositions)
			channels.forEach((channel) => {
				const colStart = channelStartPositions[channel];
				const channelData = splitRows.filter((r) => r.channel === channel);

				// ----- Title -----
				whSheet.mergeCells(cutChannelStartRow, colStart, cutChannelStartRow, colStart + 1);
				const titleCell = whSheet.getCell(cutChannelStartRow, colStart);
				titleCell.value = `Cut Qty - ${channel}`;
				titleCell.font = { bold: true, size: 14 };
				titleCell.alignment = { horizontal: "left" };

				// ----- Headers -----
				const headerRow = whSheet.getRow(cutChannelStartRow + 1);
				const headers = ["SIZE", "QTY"];

				headers.forEach((h, i) => {
					const cell = headerRow.getCell(colStart + i);
					cell.value = h;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
					cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
					cell.border = {
						top: { style: "thin", color: { argb: "FFBBBBBB" } },
						left: { style: "thin", color: { argb: "FFBBBBBB" } },
						right: { style: "thin", color: { argb: "FFBBBBBB" } },
						bottom: { style: "thin", color: { argb: "FFBBBBBB" } },
					};

					whSheet.getColumn(colStart + i).width = 14;
				});

				// ----- SIZE ROWS -----
				let rowPtr2 = cutChannelStartRow + 2;

				sizes.forEach((sizeName) => {
					const r = whSheet.getRow(rowPtr2);

					const sizeObj = channelData.find((x) => x.size === sizeName);
					const cutQty = sizeObj ? sizeObj.cut_qty : 0;

					r.getCell(colStart).value = sizeName;
					r.getCell(colStart + 1).value = cutQty;

					// styling
					[colStart, colStart + 1].forEach((c) => {
						const cell = r.getCell(c);
						cell.alignment = { horizontal: "center" };
						cell.border = {
							top: { style: "thin", color: { argb: "FFEEEEEE" } },
							left: { style: "thin", color: { argb: "FFEEEEEE" } },
							right: { style: "thin", color: { argb: "FFEEEEEE" } },
							bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
						};
					});

					rowPtr2++;
				});
			});

			// =====================================================================
			// SECTION 5: WAREHOUSE SPLIT - CHANNEL LEVEL
			// =====================================================================

			// Start 2 rows below previous section
			let whSplitStartRow = cutChannelStartRow + sizes.length + 6;

			// Get all unique warehouses from api
			const warehouses = whSplitData.result.warehouses;

			// build channel-wise warehouse percent
			channels.forEach((channel) => {
				const colStart = channelStartPositions[channel];

				// ----- Titles -----
				whSheet.mergeCells(whSplitStartRow, colStart, whSplitStartRow, colStart + 2);
				const titleCell = whSheet.getCell(whSplitStartRow, colStart);
				titleCell.value = `Warehouse Split - ${channel}`;
				titleCell.font = { bold: true, size: 14 };
				titleCell.alignment = { horizontal: "left" };

				const subTitle = whSheet.getCell(whSplitStartRow + 1, colStart);
				subTitle.value = "Ratio";
				subTitle.font = { italic: true, size: 11 };
				subTitle.alignment = { horizontal: "left" };

				// ----- Headers -----
				const headerRow = whSheet.getRow(whSplitStartRow + 2);
				const headers = ["WAREHOUSE", "QTY PERCENT"];

				headers.forEach((h, i) => {
					const cell = headerRow.getCell(colStart + i);
					cell.value = h;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
					cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
					cell.border = {
						top: { style: "thin", color: { argb: "FFBBBBBB" } },
						left: { style: "thin", color: { argb: "FFBBBBBB" } },
						right: { style: "thin", color: { argb: "FFBBBBBB" } },
						bottom: { style: "thin", color: { argb: "FFBBBBBB" } },
					};
					whSheet.getColumn(colStart + i).width = 18;
				});

				// Filter all rows for this channel across all sizes
				const channelSizeList = splitRows.filter((r) => r.channel === channel);

				// Warehouses aggregated (sum %/count)
				const whTotals = {};
				warehouses.forEach((w) => (whTotals[w] = []));

				// Collect percentage per size
				channelSizeList.forEach((sizeObj) => {
					sizeObj.ocSplitQtyChannelSizeWhSplit.forEach((w) => {
						whTotals[w.warehouse].push(w.warehouse_qty_precent || 0);
					});
				});

				// Table rows start here
				let r = whSplitStartRow + 3;

				warehouses.forEach((wh) => {
					const row = whSheet.getRow(r);

					// Average % for this warehouse
					let arr = whTotals[wh];
					let avgPct = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

					row.getCell(colStart).value = wh;
					row.getCell(colStart + 1).value = avgPct / 100;
					row.getCell(colStart + 1).numFmt = "0%";

					// Style
					[colStart, colStart + 1].forEach((c) => {
						const cell = row.getCell(c);
						cell.alignment = { horizontal: "center" };
						cell.border = {
							top: { style: "thin", color: { argb: "FFEEEEEE" } },
							left: { style: "thin", color: { argb: "FFEEEEEE" } },
							right: { style: "thin", color: { argb: "FFEEEEEE" } },
							bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
						};
					});

					r++;
				});
			});

			// =====================================================================
			// SECTION 6: SIZE QTY SPLIT (WAREHOUSE WISE)
			// =====================================================================

			// Start 2 rows after warehouse split section
			let sizeWhStartRow = whSplitStartRow + warehouses.length + 6;

			// Build one table per channel horizontally
			channels.forEach((channel) => {
				const colStart = channelStartPositions[channel];
				const chanSizes = splitRows.filter((r) => r.channel === channel);

				// ----- Title -----
				whSheet.mergeCells(sizeWhStartRow, colStart, sizeWhStartRow, colStart + warehouses.length);
				const titleCell = whSheet.getCell(sizeWhStartRow, colStart);
				titleCell.value = "Size Qty Split";
				titleCell.font = { bold: true, size: 14 };
				titleCell.alignment = { horizontal: "left" };

				// ----- Header Row 1 -----
				const hdrRow1 = whSheet.getRow(sizeWhStartRow + 1);
				hdrRow1.getCell(colStart).value = "SIZE";
				hdrRow1.getCell(colStart).font = { bold: true };
				hdrRow1.getCell(colStart).alignment = { horizontal: "center" };
				hdrRow1.getCell(colStart).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
				hdrRow1.getCell(colStart).border = {
					top: { style: "thin" },
					left: { style: "thin" },
					right: { style: "thin" },
					bottom: { style: "thin" },
				};

				// Warehouse headers
				warehouses.forEach((wh, i) => {
					const c = colStart + 1 + i;
					const cell = hdrRow1.getCell(c);
					cell.value = wh;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
					cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0D0D0" } };
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						right: { style: "thin" },
						bottom: { style: "thin" },
					};
					whSheet.getColumn(c).width = 14;
				});

				// ----- SIZE ROWS -----
				let r = sizeWhStartRow + 2;

				sizes.forEach((sizeName) => {
					const row = whSheet.getRow(r);

					row.getCell(colStart).value = sizeName;
					row.getCell(colStart).alignment = { horizontal: "center" };
					row.getCell(colStart).border = {
						top: { style: "thin", color: { argb: "FFEEEEEE" } },
						left: { style: "thin", color: { argb: "FFEEEEEE" } },
						right: { style: "thin", color: { argb: "FFEEEEEE" } },
						bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
					};

					const sizeObj = chanSizes.find((s) => s.size === sizeName);
					const whData = sizeObj ? sizeObj.ocSplitQtyChannelSizeWhSplit : [];

					// Fill warehouse qtys
					warehouses.forEach((wh, i) => {
						const qtyObj = whData.find((x) => x.warehouse === wh);
						const qtyValue = qtyObj ? qtyObj.qty : 0;

						const c = colStart + 1 + i;
						const cell = row.getCell(c);

						cell.value = qtyValue;
						cell.alignment = { horizontal: "center" };
						cell.border = {
							top: { style: "thin", color: { argb: "FFEEEEEE" } },
							left: { style: "thin", color: { argb: "FFEEEEEE" } },
							right: { style: "thin", color: { argb: "FFEEEEEE" } },
							bottom: { style: "thin", color: { argb: "FFEEEEEE" } },
						};
					});

					r++;
				});
			});
		}

		// --- Download ---
		const bufferOut = await workbook.xlsx.writeBuffer();
		const blobOut = new Blob([bufferOut], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blobOut);
		link.download = `${costData.style_ref_no || "costing"}_details.xlsx`;
		link.click();

		return true;
	} catch (err) {
		console.error("Excel generation failed:", err);
		throw new Error("Excel generation failed: " + err.message);
	}
};
